import React, { useEffect, useState } from "react";
import { Button, Form, Input } from "antd";
import { useNavigate } from "react-router-dom";
import AxiosServices from "../../Services/axiosServices";
import { showErrorModal, showMessageModal } from "../../Services/ModalServices";

/**
 * ResetPassword
 * - Reads the `token` from the URL query string
 * - Lets the user enter a new password twice
 * - Calls the API to set the new password
 * - On success, informs the user and redirects to the login page
 */
const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [token, setToken] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const queryParameters = new URLSearchParams(window.location.search);
        const t = queryParameters.get("token");
        if (t) setToken(t);
    }, []);

    type FormValues = {
        password: string;
        confirm: string;
    };

    const onFinish = async (values: FormValues) => {
        if (!token) {
            showErrorModal("Lien invalide ou expiré (token manquant).");
            return;
        }

        if (values.password !== values.confirm) {
            showErrorModal("Les mots de passe ne correspondent pas.");
            return;
        }

        try {
            setSubmitting(true);
            // Implement this service in AxiosServices if it doesn't exist yet.
            // Expected signature: resetPassword(token: string, newPassword: string)
            const result = await (AxiosServices as any).resetPassword(token, values.password);

            if (result?.error) {
                showErrorModal(result.error);
                return;
            }

            showMessageModal("Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.");
            navigate("/users/login");
        } catch (e: any) {
            showErrorModal(e?.message ?? "Une erreur est survenue lors de la réinitialisation.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto max-w-md p-6">
            <h1 className="text-2xl font-bold mb-1">Réinitialisation du mot de passe</h1>
            {/*<p className="text-sm opacity-80 mb-6">Token: <span className="font-mono break-all">{token || "(absent)"}</span></p>*/}

            <Form layout="vertical" onFinish={onFinish}>
                <Form.Item
                    label="Nouveau mot de passe"
                    name="password"
                    rules={[
                        { required: true, message: "Veuillez saisir votre nouveau mot de passe." },
                        { min: 8, message: "Au moins 8 caractères." },
                    ]}
                    hasFeedback
                >
                    <Input.Password autoComplete="new-password" />
                </Form.Item>

                <Form.Item
                    label="Confirmer le mot de passe"
                    name="confirm"
                    dependencies={["password"]}
                    hasFeedback
                    rules={[
                        { required: true, message: "Veuillez confirmer votre mot de passe." },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue("password") === value) return Promise.resolve();
                                return Promise.reject(new Error("Les mots de passe ne correspondent pas."));
                            },
                        }),
                    ]}
                >
                    <Input.Password autoComplete="new-password" />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={submitting} block>
                        Valider
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ResetPassword;
